/**
 * Google Maps Embed API helper for the location section.
 *
 * The key mirrors the one hardcoded in the production location component
 * (`src/components/partials/Accordion/AccordionLocation.js`) — kept here as a
 * local constant so the prototype stays self-contained and doesn't require
 * editing any production file. If the production key rotates, update it there
 * and here (or lift it into a shared config and import it in both).
 */
const GOOGLE_MAPS_KEY = 'AIzaSyC35C66QY50Cirolb2lPN3Gamj9qv5_a6Y';

// Sets/overrides the zoom= param on a Maps Embed URL (replaces an existing value,
// or appends one when absent). Kept for callers that need to override; the viewer
// now honours whatever zoom the backend baked into the feed's mapUrl.
export function setZoom(url, zoom) {
    if (!url) return url;
    if (/[?&]zoom=/.test(url)) return url.replace(/([?&]zoom=)[^&]*/, `$1${zoom}`);
    return `${url}${url.includes('?') ? '&' : '?'}zoom=${zoom}`;
}

// Builds a Maps Embed "place" URL from a free-text location (Google geocodes it).
// "place" mode always drops a marker/pin at the query.
export function mapEmbedUrl(query) {
    const q = encodeURIComponent(query || '');
    return `https://www.google.com/maps/embed/v1/place?key=${GOOGLE_MAPS_KEY}&q=${q}&zoom=12&language=en`;
}

// The backend leaves the Google Maps key out of the feed's `mapUrl` (it must not
// expose the key server-side). The public embed key is attached here on the client.
export function withMapKey(url) {
    if (!url) return '';
    if (/[?&]key=/.test(url)) return url;
    return `${url}${url.includes('?') ? '&' : '?'}key=${GOOGLE_MAPS_KEY}`;
}

// Returns the embed URL for the location section. The feed's own `mapUrl` is a
// Maps Embed "view" URL (center=lat,lng), which we now use as-is (only attaching
// the public key).
//
// We deliberately do NOT rewrite it to a "place" embed (q=lat,lng) any more:
// place mode reverse-geocodes the coords to a nearby Google Place, and clicking
// the resulting pin opens THAT place by its place_id — which returns a 404
// ("The provided Place ID is no longer valid") once Google's cached id goes
// stale. The view endpoint carries no place_id, so it can't 404; it centers on
// the exact coords (no marker). When the feed has no `mapUrl`, fall back to a
// geocoded "place" embed from the free-text location.
//
// The mapUrl already carries the zoom the backend chose (zoom 12 for the default
// approximate location, zoom 17 for a new development the agency set to "exact"),
// so we pass it through unchanged apart from attaching the public key.
export function pinnedEmbedUrl(mapUrl, location) {
    if (mapUrl) return withMapKey(mapUrl);
    if (location) return mapEmbedUrl(location);
    return '';
}
